import React, { useRef, useState } from 'react';
import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material';
import { Form, FormikProvider, useFormik } from 'formik';
import * as Yup from 'yup';
import * as XLSX from 'xlsx';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { Icon } from '@iconify/react/dist/iconify.js';
import GlobalDialog from '../../components/mui/Dialogue/GlobalDialog';
import { AddButton, CancelButton, SubmitButton } from '../../components/mui/Buttons/Buttons';
import Loader3D from '../../components/Loader/Loader3D';
import { getLeadsForDeo, postLeadForDeo } from '../../redux/features/deoSlice';

//THE API REQUIRES EVERY ONE OF THESE WHEN THE ROW IS NOT ON A "SIMPLE" ENROLLMENT
//COURSE - SEE createDEOLeads -> fullRequiredFields
const FULL_REQUIRED_COLUMNS = [
  'student_name',
  'age',
  'gender',
  'place',
  'email',
  'class',
  'syllubus',
  'parent_name',
  'country_code',
  'contact_number',
  'whatsapp_number',
  'remarks',
];

//A "SIMPLE" ENROLLMENT COURSE ONLY NEEDS THE BASIC CONTACT DETAILS
const SIMPLE_REQUIRED_COLUMNS = ['student_name', 'country_code', 'contact_number'];

//THE TEMPLATE IS BUILT ON EVERY DOWNLOAD, SO ITS DROPDOWNS ALWAYS HOLD THE COURSES,
//CLASSES AND SYLLABUSES THAT EXIST RIGHT NOW RATHER THAN A SAVED SNAPSHOT
const TEMPLATE_COLUMNS = [
  { header: 'student_name', width: 24 },
  { header: 'age', width: 8 },
  { header: 'place', width: 18 },
  { header: 'gender', width: 12 },
  { header: 'email', width: 28 },
  { header: 'class', width: 16 },
  { header: 'syllubus', width: 16 },
  { header: 'parent_name', width: 24 },
  { header: 'country_code', width: 14 },
  { header: 'contact_number', width: 18 },
  { header: 'whatsapp_number', width: 18 },
  { header: 'preferred_type', width: 16 },
  { header: 'course_name', width: 30 },
  { header: 'club_name', width: 24 },
  { header: 'program_name', width: 24 },
  { header: 'remarks', width: 34 },
];

//HOW FAR DOWN THE SHEET THE DROPDOWNS ARE APPLIED
const TEMPLATE_LAST_ROW = 1000;

const isBlank = (value) => value === undefined || value === null || String(value).trim() === '';
const sameText = (a, b) => String(a ?? '').trim().toLowerCase() === String(b ?? '').trim().toLowerCase();
const toNumber = (value) => (isBlank(value) ? null : Number(value));

//SOME NAMES CARRY TRAILING SPACES AND A FEW REPEAT - THE LOOKUP TRIMS AND LOWERCASES
//BEFORE COMPARING, SO THE DROPDOWN SHOWS ONE TIDY ENTRY EACH
const uniqueNames = (list, key) => {
  const seen = new Set();
  const names = [];
  (list || []).forEach((item) => {
    const name = String(item?.[key] ?? '').trim();
    const dedupeKey = name.toLowerCase();
    if (!name || seen.has(dedupeKey)) return;
    seen.add(dedupeKey);
    names.push(name);
  });
  return names;
};

const DeoLeadsBulkUpload = ({ dispatchParams }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { loading, syllabuses, classes, courses } = useSelector((store) => store.deo);
  const [open, setOpen] = useState(false);
  const fileInputRef = useRef(null);
  const [excellConvertionLoading, setExcellConvertionLoading] = useState(false);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [rowErrors, setRowErrors] = useState([]);
  const initialValues = {
    excelData: [],
  };

  //COLUMN NAMES OF public/sheets/Deo-Leads-Sheet.xlsx
  const allowedKeys = [
    'student_name',
    'age',
    'place',
    'gender',
    'email',
    'class',
    'syllubus',
    'parent_name',
    'country_code',
    'contact_number',
    'whatsapp_number',
    'preferred_type',
    'course_name',
    'club_name',
    'program_name',
    'remarks',
  ];

  //SHAPE AND FORMAT ONLY. WHICH FIELDS ARE MANDATORY DEPENDS ON THE COURSE PICKED
  //IN THE ROW, SO THAT RULE IS APPLIED IN validateRows() INSTEAD
  const schema = Yup.object({
    excelData: Yup.array()
      .of(
        Yup.object()
          .shape({
            student_name: Yup.string().trim().required('Student name is required'),
            country_code: Yup.number().typeError('Country code must be numeric').required('Country code is required'),
            contact_number: Yup.number()
              .typeError('Contact number must be numeric')
              .required('Contact number is required'),
            whatsapp_number: Yup.number().typeError('Whatsapp number must be numeric').nullable(),
            preferred_type: Yup.string().oneOf(['Course', 'Club'], 'Invalid preferred type').nullable(),
            age: Yup.number().typeError('Age must be numeric').integer('Age must be a whole number').nullable(),
            gender: Yup.string().oneOf(['Male', 'Female'], 'Invalid gender').nullable(),
            email: Yup.string()
              .transform((value) => (value === '' ? null : value))
              .email('Invalid email')
              .nullable(),
            place: Yup.string().nullable(),
            class: Yup.mixed().nullable(),
            syllubus: Yup.string().nullable(),
            parent_name: Yup.string().nullable(),
            course_name: Yup.string().nullable(),
            club_name: Yup.string().nullable(),
            program_name: Yup.string().nullable(),
            remarks: Yup.string().nullable(),
          })
          .test('no-extra-keys', 'Invalid keys found in Excel', (value) => {
            if (!value) return false;
            const keys = Object.keys(value);
            const extraKeys = keys.filter((k) => !allowedKeys.includes(k));
            return extraKeys.length === 0;
          }),
      )
      .min(1, 'Please upload at least one row in Excel')
      .required('Excel data is required'),
  });

  const findCourse = (name) => courses?.find((course) => sameText(course.courseName, name));
  const findClass = (name) => classes?.find((item) => sameText(item.className, name));
  const findSyllabus = (name) => syllabuses?.find((item) => sameText(item.name, name));

  //MIRRORS THE SERVER SIDE RULE SO A BAD SHEET IS REPORTED ROW BY ROW HERE, RATHER
  //THAN COMING BACK AS ONE MESSAGE THAT DOES NOT SAY WHICH ROW FAILED
  const validateRows = (rows) => {
    const errors = [];

    rows.forEach((item, index) => {
      const rowNumber = index + 1;
      const problems = [];
      const preferredType = String(item.preferred_type ?? '')
        .trim()
        .toLowerCase();

      let course = null;
      if (preferredType === 'course') {
        if (isBlank(item.course_name)) {
          problems.push('course_name is required when preferred_type is Course');
        } else {
          course = findCourse(item.course_name);
          if (!course) problems.push(`course "${item.course_name}" not found`);
        }
      }
      if (preferredType === 'club' && isBlank(item.club_name)) {
        problems.push('club_name is required when preferred_type is Club');
      }

      if (!isBlank(item.class) && !findClass(item.class)) {
        problems.push(`class "${item.class}" not found`);
      }
      if (!isBlank(item.syllubus) && !findSyllabus(item.syllubus)) {
        problems.push(`syllabus "${item.syllubus}" not found`);
      }

      //A "SIMPLE" COURSE RELAXES THE MANDATORY FIELDS, EXACTLY AS THE ADD LEAD MODAL DOES
      const required = course?.courseEnrollmentType === 'simple' ? SIMPLE_REQUIRED_COLUMNS : FULL_REQUIRED_COLUMNS;
      required.forEach((column) => {
        if (isBlank(item[column])) problems.push(`${column} is required`);
      });

      if (problems.length) errors.push(`Row ${rowNumber}: ${problems.join(', ')}`);
    });

    return errors;
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: schema,
    onSubmit: (values) => {
      const errors = validateRows(values.excelData);
      if (errors.length) {
        setRowErrors(errors);
        enqueueSnackbar(`${errors.length} row(s) need attention`, {
          variant: 'error',
        });
        return;
      }
      setRowErrors([]);

      const data = values.excelData.map((item) => {
        const preferredType = String(item.preferred_type ?? '')
          .trim()
          .toLowerCase();
        //THE SHEET CARRIES NAMES, THE API EXPECTS IDS FOR CLASS, SYLLABUS AND COURSE
        const course = preferredType === 'course' ? findCourse(item.course_name) : null;

        return {
          lead_name: item.student_name,
          lead_country_code: toNumber(item.country_code),
          lead_mob_no: toNumber(item.contact_number),
          lead_whatsapp_no: toNumber(item.whatsapp_number),
          age: toNumber(item.age),
          gender: isBlank(item.gender) ? null : String(item.gender).trim(),
          //0 IS THE "NOT SET" SENTINEL BOTH COLUMNS DEFAULT TO
          lead_class: findClass(item.class)?.id ?? 0,
          lead_syllabus_id: findSyllabus(item.syllubus)?.id ?? 0,
          //ONLY ONE OF THESE IS EVER SET - IT IS WHAT THE API DERIVES "Preferred Type" FROM
          lead_preffered_course: course ? course.id : null,
          lead_preffered_club: preferredType === 'club' ? String(item.club_name).trim() : null,
          lead_remarks: isBlank(item.remarks) ? null : String(item.remarks),
          lead_father_name: isBlank(item.parent_name) ? null : item.parent_name,
          lead_place: isBlank(item.place) ? null : item.place,
          lead_email_id: isBlank(item.email) ? null : item.email,
          program_name: isBlank(item.program_name) ? null : item.program_name,
        };
      });

      dispatch(postLeadForDeo({ data })).then((res) => {
        if (res.payload.status === 'error') {
          enqueueSnackbar(res.payload.message, {
            variant: 'error',
          });
        } else if (res.payload.status === 'success') {
          dispatch(getLeadsForDeo(dispatchParams));
          handleCancel();
          enqueueSnackbar(res.payload.message, {
            variant: 'success',
          });
        }
      });
    },
  });

  const handleCancel = () => {
    setOpen(false);
    setRowErrors([]);
    formik.resetForm();
  };

  const downloadSampleExcel = async () => {
    setTemplateLoading(true);
    try {
      //LOADED ON DEMAND - exceljs IS ONLY NEEDED BY THIS BUTTON, SO IT STAYS OUT OF
      //THE MAIN BUNDLE. THE PROJECT'S xlsx CANNOT WRITE DROPDOWNS.
      const ExcelJS = (await import('exceljs')).default;

      const workbook = new ExcelJS.Workbook();
      const sheet = workbook.addWorksheet('Leads');
      sheet.columns = TEMPLATE_COLUMNS.map((column) => ({
        header: column.header,
        key: column.header,
        width: column.width,
      }));
      sheet.getRow(1).font = { bold: true };
      sheet.getRow(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8EEF7' } };
      sheet.views = [{ state: 'frozen', ySplit: 1 }];

      //EACH LIST GOES ON ITS OWN SHEET AND THE DROPDOWN POINTS AT THAT RANGE - AN
      //INLINE LIST IS CAPPED AT 255 CHARACTERS, WHICH A COURSE LIST WOULD BLOW PAST
      const lookups = [
        { sheet: 'Courses', title: 'Course Name', column: 'course_name', values: uniqueNames(courses, 'courseName') },
        { sheet: 'Classes', title: 'Class Name', column: 'class', values: uniqueNames(classes, 'className') },
        { sheet: 'Syllabus', title: 'Syllabus Name', column: 'syllubus', values: uniqueNames(syllabuses, 'name') },
      ];
      lookups.forEach((lookup) => {
        const lookupSheet = workbook.addWorksheet(lookup.sheet);
        lookupSheet.columns = [{ header: lookup.title, key: 'value', width: 36 }];
        lookupSheet.getRow(1).font = { bold: true };
        lookup.values.forEach((value) => lookupSheet.addRow({ value }));
      });

      const applyDropdown = (header, formula) => {
        //ALL SIXTEEN COLUMNS SIT WITHIN A-P
        const letter = String.fromCharCode(65 + TEMPLATE_COLUMNS.findIndex((c) => c.header === header));
        for (let row = 2; row <= TEMPLATE_LAST_ROW; row += 1) {
          sheet.getCell(`${letter}${row}`).dataValidation = {
            type: 'list',
            allowBlank: true,
            formulae: [formula],
            showErrorMessage: true,
            errorStyle: 'stop',
            errorTitle: 'Invalid value',
            error: 'Pick a value from the dropdown list.',
          };
        }
      };

      applyDropdown('gender', '"Male,Female"');
      applyDropdown('preferred_type', '"Course,Club"');
      lookups.forEach((lookup) => {
        if (!lookup.values.length) return;
        //THE RANGE STOPS AT THE LAST REAL VALUE, OTHERWISE THE DROPDOWN IS PADDED
        //WITH HUNDREDS OF BLANK ENTRIES
        applyDropdown(lookup.column, `=${lookup.sheet}!$A$2:$A$${lookup.values.length + 1}`);
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'Deo_Leads_Upload_Structure.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error building the excel template:', error);
      enqueueSnackbar('Could not build the excel template', {
        variant: 'error',
      });
    } finally {
      setTemplateLoading(false);
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setExcellConvertionLoading(true);
      setRowErrors([]);
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const binaryString = e.target.result;
          const workbook = XLSX.read(binaryString, { type: 'binary' });
          //THE LEAD SHEET IS ALWAYS THE FIRST ONE - ANY FURTHER SHEETS HOLD THE LISTS
          //THAT BACK THE DROPDOWNS
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const json = XLSX.utils.sheet_to_json(worksheet);
          //SOME TEMPLATE HEADERS CARRY TRAILING SPACES (E.G. "place "), SO THE KEYS
          //ARE TRIMMED HERE INSTEAD OF EDITING THE SHEET
          const rows = json.map((row) =>
            Object.entries(row).reduce((acc, [key, value]) => {
              acc[String(key).trim()] = typeof value === 'string' ? value.trim() : value;
              return acc;
            }, {}),
          );
          formik.setFieldValue('excelData', rows);
          formik.setFieldTouched('excelData', true);
        } catch (error) {
          console.error('Error parsing Excel file:', error);
          formik.setFieldValue('excelData', []);
        } finally {
          setExcellConvertionLoading(false);
        }
      };
      reader.onerror = () => {
        console.error('File reading error');
        setExcellConvertionLoading(false);
      };
      reader.readAsBinaryString(file);
    }
  };

  return (
    <GlobalDialog
      maxWidth={'sm'}
      fullWidth
      title={'Action'}
      open={open}
      setOpen={setOpen}
      closeButton={true}
      closeCondition={handleCancel}
      popupButton={
        <>
          <AddButton size={'small'} action={() => setOpen(true)}>
            Bulk Upload
          </AddButton>
        </>
      }
      content={
        open ? (
          loading ? (
            <Loader3D />
          ) : (
            <>
              <FormikProvider value={formik}>
                <Form onSubmit={formik.handleSubmit}>
                  <Box>
                    <Stack gap={2}>
                      <Box mb={1}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={downloadSampleExcel}
                          disabled={templateLoading}
                        >
                          <Icon icon="vscode-icons:file-type-excel2" width="25" height="25" />
                          <Typography ml={'4px'} variant={'button'}>
                            {templateLoading ? 'Preparing...' : 'Download Excell Structure'}
                          </Typography>
                        </Button>
                      </Box>

                      <Box>
                        <TextField
                          name="excelData"
                          inputRef={fileInputRef}
                          InputLabelProps={{ shrink: true }}
                          placeholder="Select an Excel File to Upload"
                          label="Select an Excel File to Upload"
                          variant="outlined"
                          type="file"
                          onChange={handleFileChange}
                          accept=".xlsx, .xls"
                          error={formik.touched.excelData && Boolean(formik.errors.excelData)}
                          helperText={
                            formik.touched.excelData && formik.errors.excelData
                              ? typeof formik.errors.excelData === 'string'
                                ? formik.errors.excelData
                                : Array.isArray(formik.errors.excelData) &&
                                    typeof formik.errors.excelData[0] === 'string'
                                  ? formik.errors.excelData[0]
                                  : 'Invalid or missing data in Excel rows.'
                              : ''
                          }
                        />
                      </Box>
                      {formik.errors.excelData && Array.isArray(formik.errors.excelData) && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                          {typeof formik.errors.excelData[0] === 'string'
                            ? formik.errors.excelData
                            : formik.errors.excelData
                                .map((err, i) => (err ? `Row ${i + 1}: ${Object.values(err).join(', ')}` : ''))
                                .join(' | ')}
                        </Alert>
                      )}
                      {rowErrors.length > 0 && (
                        <Alert severity="error" sx={{ mb: 2, maxHeight: 220, overflowY: 'auto' }}>
                          <Stack gap={0.5}>
                            {rowErrors.map((error) => (
                              <Typography key={error} variant="caption">
                                {error}
                              </Typography>
                            ))}
                          </Stack>
                        </Alert>
                      )}
                    </Stack>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Stack direction={'row'} spacing={1}>
                      <CancelButton action={() => handleCancel()}>Cancel</CancelButton>

                      <SubmitButton disabled={loading || excellConvertionLoading} type="submit">
                        {loading ? 'Submiting...' : 'Submit'}
                      </SubmitButton>
                    </Stack>
                  </Box>
                </Form>
              </FormikProvider>
            </>
          )
        ) : null
      }
    />
  );
};

export default DeoLeadsBulkUpload;
